import { inject, Injectable } from '@angular/core';
import { FeatureFlagDxpService } from '@pure1/data';
import { catchError, forkJoin, map, Observable, of, shareReplay, take, tap, timeout } from 'rxjs';
import { FeatureNames } from '../../../../model/FeatureNames';

/**
 * The follow-up window the product contract names as the default. Used only
 * when the flags cannot name a window, and always together with an error log,
 * so a misconfiguration shows up in Datadog rather than as quietly wrong copy.
 */
export const EVERCYCLE_FALLBACK_FOLLOWUP_BUSINESS_DAYS = 5;

/**
 * Supported follow-up windows, one flag each. Feature flags only carry a
 * boolean, so a number is expressed by which flag is on. Adding a window means
 * adding a `FeatureNames` entry and a row here.
 */
export const EVERCYCLE_FOLLOWUP_DAYS_FLAGS: readonly { readonly days: number; readonly flag: FeatureNames }[] = [
    { days: 3, flag: FeatureNames.PROREC_EVERCYCLE_FOLLOWUP_DAYS_3 },
    { days: 5, flag: FeatureNames.PROREC_EVERCYCLE_FOLLOWUP_DAYS_5 },
    { days: 7, flag: FeatureNames.PROREC_EVERCYCLE_FOLLOWUP_DAYS_7 },
    { days: 10, flag: FeatureNames.PROREC_EVERCYCLE_FOLLOWUP_DAYS_10 },
    { days: 14, flag: FeatureNames.PROREC_EVERCYCLE_FOLLOWUP_DAYS_14 },
];

/**
 * Upper bound on waiting for the flag service. Flag reads wait for the UI
 * platform to finish loading, which can in principle never happen; the
 * confirmation copy must not hang behind it.
 */
export const EVERCYCLE_FOLLOWUP_FLAG_TIMEOUT_MS = 10_000;

/** Outcome of reading the flags, before it is reduced to a number of days. */
export type EvercycleFollowupDaysResolution =
    | { readonly kind: 'CONFIGURED'; readonly days: number }
    | { readonly kind: 'UNCONFIGURED' }
    | { readonly kind: 'CONFLICT'; readonly enabledDays: readonly number[] }
    /**
     * At least one flag could not be read. `enabledDays` holds the windows that
     * did read as enabled, so a partial failure can still honour them.
     */
    | { readonly kind: 'UNAVAILABLE'; readonly enabledDays: readonly number[]; readonly reason: string };

/**
 * Resolves the "N business days" in the EverCycle acceptance confirmation from
 * feature flags, so the window can change without a deploy.
 *
 * - Exactly one window flag on: that window.
 * - Several on: the longest of them, because promising a later follow-up
 *   than the team delivers is the safer mistake. Logged as an error.
 * - None on: the fallback window. Logged as an error.
 * - Any flag unread (the flag service failed for it, or timed out): the
 *   longest window that did read as enabled, else the fallback window. An
 *   unread flag could be the one that is on, so this is logged as an error
 *   whatever the other flags say.
 *
 * Only a read where every flag answered is cached for the session. A read
 * with any failure is not, so the next caller retries.
 */
@Injectable({ providedIn: 'root' })
export class EvercycleFollowupDaysService {
    private readonly featureFlags = inject(FeatureFlagDxpService);
    private cached$: Observable<number> | null = null;

    businessDays$(): Observable<number> {
        this.cached$ ??= this.readFlags().pipe(
            tap(resolution => {
                if (resolution.kind === 'UNAVAILABLE') {
                    this.cached$ = null;
                }
            }),
            map(resolution => toBusinessDays(resolution)),
            shareReplay(1),
        );
        return this.cached$;
    }

    private readFlags(): Observable<EvercycleFollowupDaysResolution> {
        return forkJoin(
            EVERCYCLE_FOLLOWUP_DAYS_FLAGS.map(({ days, flag }) =>
                this.featureFlags.getFeatureFlag(flag).pipe(
                    take(1),
                    map(status => ({ days, status })),
                ),
            ),
        ).pipe(
            timeout(EVERCYCLE_FOLLOWUP_FLAG_TIMEOUT_MS),
            map((results): EvercycleFollowupDaysResolution => {
                const enabledDays = results.filter(r => r.status?.enabled === true).map(r => r.days);
                // The flag service reports a failed read as `null`, never as an error.
                // An unread flag could be the enabled one, so a partial read is never
                // taken as a clean answer. A flag that does not exist comes back with
                // no entry (`undefined`); that is an answer ("not enabled"), not a
                // failed read, so a window flag nobody has created yet is simply off.
                const unreadDays = results.filter(r => r.status === null).map(r => r.days);
                if (unreadDays.length > 0) {
                    return {
                        kind: 'UNAVAILABLE',
                        enabledDays,
                        reason: `no answer for the ${unreadDays.join(', ')} day flag(s)`,
                    };
                }
                if (enabledDays.length === 1) {
                    return { kind: 'CONFIGURED', days: enabledDays[0] };
                }
                if (enabledDays.length > 1) {
                    return { kind: 'CONFLICT', enabledDays };
                }
                return { kind: 'UNCONFIGURED' };
            }),
            catchError((err: unknown) =>
                of<EvercycleFollowupDaysResolution>({
                    kind: 'UNAVAILABLE',
                    enabledDays: [],
                    reason: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
                }),
            ),
        );
    }
}

/** Reduces a resolution to the number shown to the user, logging every case that is not a clean read. */
export function toBusinessDays(resolution: EvercycleFollowupDaysResolution): number {
    switch (resolution.kind) {
        case 'CONFIGURED':
            return resolution.days;
        case 'CONFLICT': {
            const days = Math.max(...resolution.enabledDays);
            console.error(
                `EverCycle: several follow-up window flags are enabled (${resolution.enabledDays.join(', ')} days); using ${days}`,
            );
            return days;
        }
        case 'UNCONFIGURED':
            console.error(
                `EverCycle: no follow-up window flag is enabled; using ${EVERCYCLE_FALLBACK_FOLLOWUP_BUSINESS_DAYS} days`,
            );
            return EVERCYCLE_FALLBACK_FOLLOWUP_BUSINESS_DAYS;
        case 'UNAVAILABLE': {
            const days =
                resolution.enabledDays.length > 0
                    ? Math.max(...resolution.enabledDays)
                    : EVERCYCLE_FALLBACK_FOLLOWUP_BUSINESS_DAYS;
            console.error(
                `EverCycle: follow-up window flags could not all be read (${resolution.reason}); using ${days} days`,
            );
            return days;
        }
        default: {
            const unhandled: never = resolution;
            throw new Error(`EverCycle: unhandled follow-up window resolution ${JSON.stringify(unhandled)}`);
        }
    }
}
